import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { Markdown } from '@/components/Markdown/Markdown';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { formatDate, formatPersonName } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';
import {
  useChatMessages,
  useSendChatMessage,
  useCancelChatMessage,
  useClearChatThread,
  useMemberMemories,
} from '../hooks';
import { chatStreamUrl } from '../api';
import { RememberPin } from './RememberPin';
import { MemoryChip } from './MemoryChip';
import { ChatAttachmentPicker, type PendingAttachment } from './ChatAttachmentPicker';
import { ClearChatDialog } from './ClearChatDialog';
import type { ChatMessage, ClearChatOptions, Member } from '../schemas';
import styles from './ChatTab.module.css';

export function ChatTab({ member }: { member: Member }) {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  const { data, isLoading, refetch } = useChatMessages(member.id);
  const send = useSendChatMessage(member.id);
  const cancel = useCancelChatMessage();
  const clear = useClearChatThread(member.id);
  const [draft, setDraft] = useState('');
  const [liveMessages, setLiveMessages] = useState<Record<string, ChatMessage>>({});
  const [sendError, setSendError] = useState<string | null>(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const hasPendingUploads = pendingAttachments.some((p) => !p.attachment && !p.error);
  const threadEndRef = useRef<HTMLDivElement>(null);

  const merged: ChatMessage[] = useMemo(() => {
    const items = data?.items ?? [];
    const map = new Map(items.map((m) => [m.id, m]));
    for (const [id, m] of Object.entries(liveMessages)) {
      map.set(id, m);
    }
    // Sort by createdAt asc. When timestamps tie — the server can insert the
    // user message and its pending reply within the same millisecond — fall
    // back to sender role so the user's question always appears above the
    // member's reply (the user message logically caused the response).
    // Without this tiebreaker, JS's stable sort preserves whatever order the
    // Map produced, which leaks the listing endpoint's DESC ordering and
    // flips bubbles after the first round.
    return Array.from(map.values()).sort((a, b) => {
      if (a.createdAt < b.createdAt) return -1;
      if (a.createdAt > b.createdAt) return 1;
      const senderRank = senderOrder(a.sender) - senderOrder(b.sender);
      if (senderRank !== 0) return senderRank;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }, [data?.items, liveMessages]);

  useEffect(() => {
    if (!tenantId) return;
    const es = new EventSource(chatStreamUrl(tenantId, member.id));

    function patch(id: string, fields: Partial<ChatMessage>) {
      setLiveMessages((prev) => {
        const existing = prev[id];
        const base: ChatMessage = existing ?? {
          id,
          threadId: '',
          sender: 'member',
          kind: 'text',
          content: '',
          payload: null,
          status: 'streaming',
          failureReason: null,
          inputTokens: null,
          outputTokens: null,
          clientMessageId: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return { ...prev, [id]: { ...base, ...fields } };
      });
    }

    es.addEventListener('snapshot', (ev: MessageEvent) => {
      const { messageId, status, kind, content, payload } = JSON.parse(ev.data as string) as {
        messageId: string;
        status: ChatMessage['status'];
        kind: ChatMessage['kind'];
        content: string;
        payload: unknown;
      };
      patch(messageId, { status, kind, content, payload });
    });
    es.addEventListener('chunk', (ev: MessageEvent) => {
      const { messageId, delta } = JSON.parse(ev.data as string) as {
        messageId: string;
        delta: string;
      };
      setLiveMessages((prev) => {
        const existing = prev[messageId];
        if (!existing) return prev;
        return { ...prev, [messageId]: { ...existing, content: existing.content + delta } };
      });
    });
    es.addEventListener('complete', (ev: MessageEvent) => {
      const { messageId, content, inputTokens, outputTokens } = JSON.parse(ev.data as string) as {
        messageId: string;
        content: string;
        inputTokens: number | null;
        outputTokens: number | null;
      };
      patch(messageId, { status: 'complete', content, inputTokens, outputTokens });
      void refetch();
    });
    es.addEventListener('failed', (ev: MessageEvent) => {
      const { messageId, reason } = JSON.parse(ev.data as string) as {
        messageId: string;
        reason: string;
      };
      patch(messageId, { status: 'failed', failureReason: reason });
    });
    es.addEventListener('cancelled', (ev: MessageEvent) => {
      const { messageId } = JSON.parse(ev.data as string) as { messageId: string };
      patch(messageId, { status: 'cancelled' });
    });
    // Tolerate unknown event types (spec §5.3) — noop handler required to suppress default dispatch.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    es.onmessage = () => {};

    return () => es.close();
  }, [tenantId, member.id, refetch]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [merged.length, merged[merged.length - 1]?.content]);

  const inflight = merged.find(
    (m) => m.sender === 'member' && (m.status === 'pending' || m.status === 'streaming'),
  );

  function onSend() {
    const content = draft.trim();
    if (!content) return;
    setSendError(null);
    const clientMessageId = crypto.randomUUID();
    const attachment_ids = pendingAttachments
      .filter((p) => p.attachment)
      .map((p) => p.attachment!.id);
    send.mutate(
      { content, clientMessageId, ...(attachment_ids.length > 0 ? { attachment_ids } : {}) },
      {
        onSuccess: ({ userMessage, pendingReply }) => {
          setDraft('');
          setPendingAttachments([]);
          setLiveMessages((prev) => ({
            ...prev,
            [userMessage.id]: userMessage,
            ...(pendingReply ? { [pendingReply.id]: pendingReply } : {}),
          }));
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setSendError(t('A reply is still being prepared. Please wait.'));
          } else if (err instanceof ApiError && err.status === 403) {
            setSendError(t("You don't have permission to chat with this Member."));
          } else if (err instanceof NetworkError) {
            setSendError(t("Couldn't reach the server. Check your connection and try again."));
          } else {
            setSendError(t('Send failed. Try again.'));
          }
        },
      },
    );
  }

  function onCancel() {
    if (!inflight) return;
    cancel.mutate({ messageId: inflight.id });
  }

  function onConfirmClear(options: ClearChatOptions) {
    setClearError(null);
    clear.mutate(options, {
      onSuccess: () => {
        setLiveMessages({});
        setPendingAttachments([]);
        setDraft('');
        setClearDialogOpen(false);
        void refetch();
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setClearError(t('Cannot clear chat while a reply is being prepared.'));
        } else if (err instanceof NetworkError) {
          setClearError(t("Couldn't reach the server. Check your connection and try again."));
        } else {
          setClearError(t('Clear failed. Try again.'));
        }
      },
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  // Build a set of message ids that have already been pinned, so the
  // Remember pill on each bubble can show its persisted state across reloads.
  const memberMemories = useMemberMemories(member.id);
  const pinnedSourceIds = useMemo(() => {
    const set = new Set<string>();
    for (const m of memberMemories.data?.items ?? []) {
      if (m.source.kind === 'chat_pin') set.add(m.source.id);
    }
    return set;
  }, [memberMemories.data]);

  // Insert a day-separator before the first message of each calendar day.
  const renderedThread: React.ReactNode[] = [];
  let lastDayKey = '';
  for (const message of merged) {
    const date = new Date(message.createdAt);
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    if (dayKey !== lastDayKey) {
      renderedThread.push(
        <div key={`day-${dayKey}`} className={styles.daySeparator}>
          {dayLabel(date)}
        </div>,
      );
      lastDayKey = dayKey;
    }
    renderedThread.push(
      <Bubble
        key={message.id}
        message={message}
        member={member}
        initiallyPinned={pinnedSourceIds.has(message.id)}
      />,
    );
  }

  return (
    <div className={styles.chat}>
      <div className={styles.chatHeader}>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          disabled={send.isPending || hasPendingUploads || clear.isPending}
          onClick={() => {
            setClearError(null);
            setClearDialogOpen(true);
          }}
        >
          {t('Clear chat')}
        </Button>
      </div>

      {clearError ? <Alert variant="error">{clearError}</Alert> : null}
      <ClearChatDialog
        open={clearDialogOpen}
        memberName={formatPersonName(member)}
        onCancel={() => setClearDialogOpen(false)}
        onConfirm={onConfirmClear}
        pending={clear.isPending}
      />

      <div className={styles.thread} aria-live="polite">
        {isLoading ? (
          <div className={styles.threadStatus}>{t('Loading conversation…')}</div>
        ) : merged.length === 0 ? (
          <div className={styles.threadEmpty}>
            {t('Start a conversation with {name}.', { name: formatPersonName(member) })}
          </div>
        ) : (
          renderedThread
        )}
        <div ref={threadEndRef} />
      </div>

      {sendError ? <Alert variant="error">{sendError}</Alert> : null}

      <form
        className={styles.composer}
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
      >
        <input
          type="text"
          className={styles.composerInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('Message {name}…', { name: member.first_name })}
          disabled={send.isPending}
          aria-label={t('Message {name}', { name: member.first_name })}
        />
        <ChatAttachmentPicker attachments={pendingAttachments} onChange={setPendingAttachments} />
        {inflight ? (
          <Button
            variant="secondary"
            size="md"
            type="button"
            onClick={onCancel}
            disabled={cancel.isPending}
          >
            {t('Stop')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            type="submit"
            isLoading={send.isPending}
            disabled={!draft.trim() || send.isPending || hasPendingUploads}
            className={styles.sendBtn}
          >
            <span aria-hidden="true" className={styles.sendIcon}>
              ➤
            </span>
            {t('Send')}
          </Button>
        )}
      </form>
    </div>
  );
}

function Bubble({
  message,
  member,
  initiallyPinned = false,
}: {
  message: ChatMessage;
  member: Member;
  initiallyPinned?: boolean;
}) {
  if (message.sender === 'system') {
    return <div className={styles.systemPill}>{message.content}</div>;
  }
  const isUser = message.sender === 'user';
  const time = formatTime(new Date(message.createdAt));
  if (isUser) {
    return (
      <div className={styles.rowUser}>
        <div className={styles.bubbleUser}>
          <Markdown className={styles.bubbleContent} content={message.content} />
          {message.status === 'failed' ? (
            <div className={styles.bubbleErrorOnDark}>
              {t('This reply failed. Try sending again.')}
            </div>
          ) : null}
          {message.status === 'cancelled' ? (
            <div className={styles.bubbleNoteOnDark}>{t('Cancelled')}</div>
          ) : null}
        </div>
        <div className={styles.timestampRight}>{time}</div>
      </div>
    );
  }
  const canRemember = message.status === 'complete' && message.kind === 'text' && message.content;
  return (
    <div className={styles.rowMember}>
      <div className={styles.avatarColumn}>
        <Avatar name={formatPersonName(member)} size="sm" kind="ai" />
        <div className={styles.timestampLeft}>{time}</div>
      </div>
      <div className={styles.bubbleMember}>
        {message.content ? (
          <Markdown className={styles.bubbleContent} content={message.content} />
        ) : message.status === 'pending' ? (
          <div className={styles.bubbleContent}>{t('Thinking…')}</div>
        ) : null}
        {message.status === 'failed' ? (
          <div className={styles.bubbleError}>{t('This reply failed. Try sending again.')}</div>
        ) : null}
        {message.status === 'cancelled' ? (
          <div className={styles.bubbleNote}>{t('Cancelled')}</div>
        ) : null}
        {message.status === 'complete' ? <MemoryChip chatMessageId={message.id} /> : null}
        {canRemember ? (
          <RememberPin member={member} chatMessage={message} initiallyPinned={initiallyPinned} />
        ) : null}
      </div>
    </div>
  );
}

function formatTime(date: Date): string {
  return formatDate(date, { hour: 'numeric', minute: '2-digit', hour12: false });
}

function dayLabel(date: Date): string {
  const today = new Date();
  if (isSameDay(date, today)) return t('Today');
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, yesterday)) return t('Yesterday');
  if (date.getFullYear() === today.getFullYear()) {
    return formatDate(date, { month: 'short', day: 'numeric' });
  }
  return formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function senderOrder(sender: ChatMessage['sender']): number {
  if (sender === 'user') return 0;
  if (sender === 'member') return 1;
  return 2; // system
}
